import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {ColumnFiltersState} from "@tanstack/react-table";

interface UserState {
    elements: UserElement[];
    filteredElements: UserElement[]; // Add filteredElements to store the filtered data
    currentPage: number;
    rowsPerPage: number;
    error: null | string;
    totalRows: number;
    sort: string[];
    loading: boolean;
    filter: { [key: string]: any }; // Store filter criteria (e.g., { username: 'admin' })
}

interface Tenant {
    _id:string
    businessName: string
}

export interface UserElement {
    _id: string
    username: string,
    email: string,
    password: string,
    enabled: boolean,
    tenantId: Tenant | null,
    roles: string
}
interface PageableDto {
    page: number;
    size: number;
    sort: string[];
}

export interface GetAllElementPayloadUser {
    pageableDto: PageableDto;
    filters: ColumnFiltersState | null

}

const initialState: UserState = {
    elements: [],
    filteredElements: [],
    currentPage: 0,
    rowsPerPage: 10,
    error: null,
    totalRows: 0,
    sort: ['dateCreated', 'asc'],
    loading: false,
    filter: {} // Initialize filter as an empty object
}

export const User = createSlice({
    name: "user",
    initialState,
    reducers: {
        getAllElement: (state, action: PayloadAction<GetAllElementPayloadUser>) => {
            state.loading = true;
        },

        getAllElementSuccess: (state, action: PayloadAction<{ data: UserElement[], currentPage: number, totalRows: number }>) => {
            state.elements = action.payload.data;
            state.filteredElements = action.payload.data; // Set filteredElements to the full data initially
            state.currentPage = action.payload.currentPage;
            state.totalRows = action.payload.totalRows;
            state.loading = false;
        },

        getAllElementError: (state) => {state.loading = false;},

        setPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },

        setRowsPerPage: (state, action: PayloadAction<number>) => {
            state.rowsPerPage = action.payload;
        },

        setSort: (state, action: PayloadAction<string[]>) => {
            state.sort = action.payload;
        },

        setFilter: (state, action: PayloadAction<{ [key: string]: any }>) => {
            state.filter = action.payload; // Set the filter criteria
            state.filteredElements = filterData(state.elements, state.filter); // Apply the filter
        },
        filterElement: (state, action: PayloadAction<{ [key: string]: any }>) => {
            state.loading = true; // Start loading while filtering
        },

        filterElementSuccess: (state, action: PayloadAction<{ data: UserElement[], currentPage: number, totalRows: number }>) => {
            state.filteredElements = action.payload.data; // Aggiorna i dati filtrati
            state.currentPage = action.payload.currentPage; // Aggiorna la pagina corrente
            state.totalRows = action.payload.totalRows; // Aggiorna il conteggio totale delle righe
            state.loading = false;
        },

        filterElementError: (state) => {
            state.loading = false;
            state.error = "Error applying filter.";
            // Stop loading if an error occurs
        },

        getAllElementUserNotPageable: (state) => {},
        getAllElementSuccessUserNotPageable: (state, action: PayloadAction<UserElement[]>) => {
            state.elements = action.payload;
            state.filteredElements = action.payload; // No pagination, set directly
        },
        getAllElementErrorUserNotPageable: (state) => {state.loading = false;},

        // fare una gestione per salvataggio admin e user
        saveElement: (state, action: PayloadAction<{ data:UserElement , callback: () => void  }>) => {},
        saveElementSuccess: (state, actions: PayloadAction<UserElement>) => {
            state.elements.push(actions.payload);
            state.filteredElements.push(actions.payload); // Add to both elements and filteredElements
        },
        saveElementError: (state, action: PayloadAction<UserElement>) => {state.loading = false;},

       saveElementUser:(state , action: PayloadAction<UserElement>) => {},
       saveElementUserSuccess:(state , action: PayloadAction<UserElement>) => {
            const payload = {
                ...action.payload,
                roles: action.payload.roles = "USER"
            }

           state.elements.push(payload);
           state.filteredElements.push(payload); // Add to both elements and filteredElements

       },
       saveElementUserError:(state, action: PayloadAction<UserElement>) => {state.loading = false;},


        updateElement: (state, actions: PayloadAction<{ data:UserElement , callback: () => void  }>) => {},
        updateElementSuccess: (state, actions: PayloadAction<UserElement>) => {
            let index = state.elements.findIndex(element => element._id === actions.payload._id);
            state.elements[index] = actions.payload;
            state.filteredElements[index] = actions.payload; // Update filtered data as well
        },
        updateElementError: (state) => {state.loading = false;},

        // update di user da parte di Admin
        updateElementAdmin: (state, actions: PayloadAction<UserElement>) => {},
        updateElementSuccessAdmin: (state, actions: PayloadAction<UserElement>) => {
            let index = state.elements.findIndex(element => element._id === actions.payload._id);
            state.elements[index] = actions.payload;
            state.filteredElements[index] = actions.payload;
        },
        updateElementErrorAdmin: (state) => {state.loading = false;},

        deleteElement: (state, action: PayloadAction<{ id: string, callback: () => void }>) => {},
        deleteElementSuccess: (state, action: PayloadAction<string>) => {
            state.elements = state.elements.filter(el => el._id !== action.payload);
            state.filteredElements = state.filteredElements.filter(el => el._id !== action.payload); // Remove from filtered data too
        },
        deleteElementError: (state) => {state.loading = false;},
    }
})


const filterData = (data: UserElement[], filter: { [key: string]: any }) => {
    return data.filter(element => {
        for (const key in filter) {
            if (filter.hasOwnProperty(key)) {

                const elementValue = element[key as keyof UserElement];
                const filterValue = filter[key];


                if (filterValue !== undefined && filterValue !== null && elementValue !== filterValue) {
                    return false;
                }
            }
        }
        return true;
    });
};



export const {
    getAllElement,
    getAllElementSuccess,
    getAllElementError,
    setPage,
    setRowsPerPage,
    setSort,
    filterElement,
    filterElementSuccess,
    filterElementError,
    setFilter, // Dispatch this to update the filter
    getAllElementUserNotPageable,
    getAllElementSuccessUserNotPageable,
    getAllElementErrorUserNotPageable,
    saveElement,
    saveElementSuccess,
    saveElementError,
    updateElement,
    updateElementSuccess,
    updateElementError,
    deleteElement,
    deleteElementSuccess,
    deleteElementError,
    saveElementUserError,
    saveElementUserSuccess,
    saveElementUser,
    updateElementAdmin,
    updateElementErrorAdmin,
    updateElementSuccessAdmin
} = User.actions;

export default User.reducer;
